import React from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import { MembershipStore, MembershipTypeStore , MembershipGroupStore } from '@seedlang/stores';
import InPlaceText from 'components/form/in_place_text';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceSelect from 'components/form/in_place_select';
import autobind from 'autobind-decorator';
import Cookies from 'js-cookie';
import SeedlangOnly from 'utils/seedlang_only';
import Spinner from 'components/spinner';
import styled from '@emotion/styled';
import WhitelabelOnly from 'utils/whitelabel_only';
import Grid from 'components/layout/grid';

const Row = styled.div`
  grid-column: 1 / -1;
`;

@observer
class MembershipEdit extends React.Component {
  static defaultProps = {
    whiteLabeled: false,
  };

  componentDidMount() {
    this.getMembershipTypes();
    this.getMembership();
    if (!MembershipGroupStore.hasIndexData) {
      MembershipGroupStore.getIndex();
    }
  }

  @autobind getMembershipTypes() {
    MembershipTypeStore.getIndex();
  }

  @autobind getMembership() {
    MembershipStore.getShow({ids: {membershipId: this.props.params.membershipId}});
  }

  @computed get filteredMembershipTypes() {
    const siteId = Cookies.get('site_id');

    return MembershipTypeStore.indexData.filter(item => item.siteId === siteId);
  }

  @computed get filteredMembershipGroups() {
    const siteId = Cookies.get('site_id');

    return MembershipGroupStore.indexData.filter(item => item.siteId === siteId);
  }

  render() {
    if (MembershipStore.hasOutstandingRequests) {
      return <Spinner />;
    }

    return (
      <Grid columns={2} gap='xs'>
        <InPlaceText
          legend='Name (Internal)'
          defaultValue={MembershipStore.showDataField('name')}
          model='memberships'
          field='name'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceText
          legend='Title (External)'
          defaultValue={MembershipStore.showDataField('title')}
          model='memberships'
          field='title'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceSelect
          legend='Membership Group'
          includeBlank
          id={MembershipStore.showDataField('id')}
          model='memberships'
          field='membership_group_id'
          options={this.filteredMembershipGroups.map(item => [item.id, item.name])}
          value={MembershipStore.showDataField('membershipGroupId')}
          afterChange={this.getMembership}
        />
        <InPlaceSelect
          legend='Membership Type'
          includeBlank
          id={MembershipStore.showDataField('id')}
          model='memberships'
          field='membershipTypeId'
          options={this.filteredMembershipTypes.map(item => [item.id, item.name])}
          value={MembershipStore.showDataField('membershipTypeId')}
          afterChange={this.getMembership}
        />
        <SeedlangOnly>
          <InPlaceText
            legend='# Months'
            defaultValue={MembershipStore.showDataField('numberMonths')}
            model='memberships'
            field='number_months'
            id={MembershipStore.showDataField('id')}
          />
        </SeedlangOnly>
        <WhitelabelOnly>
          <InPlaceSelect
            legend='Frequency'
            id={MembershipStore.showDataField('id')}
            model='memberships'
            field='number_months'
            options={[['1', 'Monthly'], ['12', 'Yearly']]}
            value={MembershipStore.showDataField('numberMonths')}
            afterChange={this.getMembership}
          />
        </WhitelabelOnly>
        <InPlaceText
          legend='Highlight Text'
          defaultValue={MembershipStore.showDataField('highlightText')}
          model='memberships'
          field='highlight_text'
          id={MembershipStore.showDataField('id')}
        />
        <SeedlangOnly>
          <InPlaceText
            legend='Icon'
            defaultValue={MembershipStore.showDataField('icon')}
            model='memberships'
            field='icon'
            id={MembershipStore.showDataField('id')}
          />
        </SeedlangOnly>
        <Row>
          <fieldset>
            <legend>Settings</legend>
            <div>
              <InPlaceCheckbox
                value={MembershipStore.showDataField('active')}
                model='memberships'
                field='active'
                id={MembershipStore.showDataField('id')}
              >
                Active
              </InPlaceCheckbox>
            </div>
            <div>
              <InPlaceCheckbox
                value={MembershipStore.showDataField('default')}
                model='memberships'
                field='default'
                id={MembershipStore.showDataField('id')}
              >
                Default
              </InPlaceCheckbox>
            </div>
            <div>
              <InPlaceCheckbox
                value={MembershipStore.showDataField('highlight')}
                model='memberships'
                field='highlight'
                id={MembershipStore.showDataField('id')}
              >
                Highlight
              </InPlaceCheckbox>
            </div>
            <div>
              <InPlaceCheckbox
                value={MembershipStore.showDataField('recurring')}
                model='memberships'
                field='recurring'
                id={MembershipStore.showDataField('id')}
              >
                Recurring
              </InPlaceCheckbox>
            </div>
            <InPlaceCheckbox
              id={MembershipStore.showData.id}
              value={MembershipStore.showData.podcastAccess}
              model='memberships'
              field='podcast_access'
            >
              Podcast Access
            </InPlaceCheckbox>
          </fieldset>
        </Row>
        <InPlaceText
          legend='Description (USD)'
          defaultValue={MembershipStore.showDataField('descriptionUsd')}
          model='memberships'
          field='description_usd'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceText
          legend='Description (EUR)'
          defaultValue={MembershipStore.showDataField('descriptionEur')}
          model='memberships'
          field='description_eur'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceText
          legend='Cost (USD)'
          defaultValue={MembershipStore.showDataField('costUsd')}
          model='memberships'
          field='cost_usd'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceText
          legend='Cost (EUR)'
          defaultValue={MembershipStore.showDataField('costEur')}
          model='memberships'
          field='cost_eur'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceText
          legend='Monthly Cost (USD)'
          defaultValue={MembershipStore.showDataField('costMonthlyUsd')}
          model='memberships'
          field='cost_monthly_usd'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceText
          legend='Monthly Cost (EUR)'
          defaultValue={MembershipStore.showDataField('costMonthlyEur')}
          model='memberships'
          field='cost_monthly_eur'
          id={MembershipStore.showDataField('id')}
        />
        <InPlaceSelect
          legend='Stripe Account'
          includeBlank
          id={MembershipStore.showDataField('id')}
          model='memberships'
          field='stripeAccount'
          options={[['DE', 'Seedlang Germany'], ['US', 'Seedlang USA'], ['EL', 'Easy Languages']]}
          value={MembershipStore.showDataField('stripeAccount')}
          afterChange={this.getMembership}
        />
        <InPlaceSelect
          legend='PayPal Account'
          includeBlank
          id={MembershipStore.showDataField('id')}
          model='memberships'
          field='paypalAccount'
          options={[['DE', 'Seedlang Germany'], ['US', 'Seedlang USA']]}
          value={MembershipStore.showDataField('paypalAccount')}
          afterChange={this.getMembership}
        />
        <fieldset>
          <legend>Test Stripe Ids (Recurring)</legend>
          <Row>
            <Grid columns={[1, 3]} gap='sm'>
              <div>USD</div>
              <InPlaceText
                defaultValue={MembershipStore.showDataField('stripeUsdTestPlanId')}
                model='memberships'
                field='stripe_usd_test_plan_id'
                id={MembershipStore.showDataField('id')}
              />
              <div>EUR</div>
              <InPlaceText
                defaultValue={MembershipStore.showDataField('stripeEurTestPlanId')}
                model='memberships'
                field='stripe_eur_test_plan_id'
                id={MembershipStore.showDataField('id')}
              />
            </Grid>
          </Row>
        </fieldset>
        <fieldset>
          <legend>Live Stripe Ids (Recurring)</legend>
          <Row>
            <Grid columns={[1, 3]} gap='sm'>
              <div>USD</div>
              <InPlaceText
                defaultValue={MembershipStore.showDataField('stripeUsdLivePlanId')}
                model='memberships'
                field='stripe_usd_live_plan_id'
                id={MembershipStore.showDataField('id')}
              />
              <div>EUR</div>
              <InPlaceText
                defaultValue={MembershipStore.showDataField('stripeEurLivePlanId')}
                model='memberships'
                field='stripe_eur_live_plan_id'
                id={MembershipStore.showDataField('id')}
              />
            </Grid>
          </Row>
        </fieldset>
        <SeedlangOnly>
          <InPlaceText
            legend='iOS ID'
            defaultValue={MembershipStore.showDataField('iosSubscriptionId')}
            model='memberships'
            field='ios_subscription_id'
            id={MembershipStore.showDataField('id')}
          />
          <InPlaceText
            legend='Android ID'
            defaultValue={MembershipStore.showDataField('androidSubscriptionId')}
            model='memberships'
            field='android_subscription_id'
            id={MembershipStore.showDataField('id')}
          />
        </SeedlangOnly>
      </Grid>
    );
  }
}

export default ErrorBoundary(MembershipEdit);
